// Reads and writes for S8 and S9 (M4's apps API, M2's settings values). Every settings
// write answers with the whole AppView, which replaces the cached ['app', appId] (lib/queries.ts).
import type { AppView, InstallInfo } from '@feedback/shared/contracts/apps';
import type { SettingView } from '@feedback/shared/contracts/settings';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, seg } from '@/lib/api';

export const installQuery = (appId: string) =>
  queryOptions({
    queryKey: ['app', appId, 'install'],
    queryFn: () => api.get<InstallInfo>(`/api/apps/${seg(appId)}/install`),
  });

/** App-level settings plus environment-level ones for one environment (defaults without one). */
export const appSettingsQuery = (appId: string, environmentId?: string) =>
  queryOptions({
    queryKey: ['app', appId, 'settings', environmentId ?? null],
    queryFn: () =>
      api.get<SettingView[]>(`/api/apps/${seg(appId)}/settings-values`, environmentId ? { environmentId } : undefined),
  });

/**
 * A settings write: runs `call`, puts the returned AppView in the cache and says so. Errors are
 * returned to the caller (thrown from mutateAsync) for inline display.
 */
export function useAppWrite<TVars>(appId: string, call: (vars: TVars) => Promise<AppView>, success: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    // Errors show inline next to the field (not as the shell's toast).
    meta: { silent: true },
    onSuccess: (view) => {
      queryClient.setQueryData(['app', appId], view);
      void queryClient.invalidateQueries({ queryKey: ['apps'] });
      void queryClient.invalidateQueries({ queryKey: ['app', appId, 'install'] });
      toast.success(success);
    },
  });
}

/** Saving one app or environment setting (PUT /api/apps/:appId/settings-values/:code). */
export function useSettingSaver(appId: string) {
  const queryClient = useQueryClient();
  return {
    async save(setting: SettingView, value: string) {
      const saved = await api.put<SettingView>(`/api/apps/${seg(appId)}/settings-values/${seg(setting.code)}`, {
        value,
        ...(setting.environmentId ? { environmentId: setting.environmentId } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['app', appId, 'settings'] });
      toast.success(`Saved: ${setting.description.replace(/ \(proposed\)$/, '')}`);
      return saved;
    },
  };
}

export const appPath = (appId: string, rest = '') => `/api/apps/${seg(appId)}${rest}`;
